import React, { PureComponent } from 'react';

import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';
import Truncate from 'react-truncate';
import { compose } from 'redux';

import Avatar from '@atlaskit/avatar';
import { gridSize } from '@atlaskit/theme';
import { RelativeDate } from '@atlassian/bitkit-date';

import Announcer from 'src/components/announcer/src/announcer';
import { withApdex } from 'src/components/apdex/with-apdex';
import PageableTable, {
  TableCell,
  TableHeaderMobileHidden,
  TableCellMobileHidden,
} from 'src/components/pageable-table';
import { withStatsd } from 'src/components/performance-metrics/with-statsd';
import RepositoryBuildStatus from 'src/components/repository-table/repository-build-status';
import SortButton from 'src/components/sort-button';
import { Repository } from 'src/components/types';
import { Link } from 'src/router/components';
import EmptyResults from 'src/sections/workspace/components/empty-results';
import { ApdexTask } from 'src/types/apdex';
import { getPagination } from 'src/utils/get-pagination';

import { WorkspaceRepositoryPagination } from '../containers/workspace-repository-pagination';

import * as styles from './profile-repositories-table.style';
import messages from './profile-repositories.i18n';

export type Props = {
  repositories: Repository[];
  pagelen: number | undefined;
  size: number | undefined;
  isLoading: boolean;
  isError: boolean;
  intl: InjectedIntl;
  getPaginationUrl: (selectedPage: number) => string;
  page: number | null;
  isAscending: boolean;
  handleSortChange: () => void;
  isFilterApplied: boolean;
  workspaceSlug: string;
  createUrl: string | undefined;
  importUrl: string | undefined;
  buildStatusesFetched: boolean;
};

const instrument = compose(
  withApdex(ApdexTask.ProfileRepositories),
  withStatsd('performance.profile-repositories-list.rendered')
);

export const InstrumentedProfileRepositoriesTable = instrument(PageableTable);

export class ProfileRepositoriesTable extends PureComponent<Props> {
  renderHeaders = (): JSX.Element => (
    <tr>
      <styles.RepositoryInfoTableHeader width="28%">
        <FormattedMessage {...messages.tableHeaderRepository} />
      </styles.RepositoryInfoTableHeader>
      <TableHeaderMobileHidden width="45%">
        <FormattedMessage {...messages.tableHeaderDescription} />
      </TableHeaderMobileHidden>
      <TableHeaderMobileHidden
        aria-sort={this.props.isAscending ? 'ascending' : 'descending'}
        width={`${gridSize() * 16}px`}
      >
        <SortButton
          onClick={this.props.handleSortChange}
          isAscending={this.props.isAscending}
        />
      </TableHeaderMobileHidden>
      <styles.BuildStatusTableHeader
        width={`${gridSize() * 8}px`}
        textAlign="center"
      >
        <FormattedMessage {...messages.tableHeaderBuilds} />
      </styles.BuildStatusTableHeader>
    </tr>
  );

  // @ts-ignore TODO: fix noImplicitAny error here
  getLastUpdatedDate(repository: Repository): JSX.Element | null {
    const { uuid, updated_on: updated, created_on: created } = repository;
    return updated ? (
      <RelativeDate key={`date-key-${uuid}`} date={new Date(updated)} />
    ) : created ? (
      <RelativeDate key={`date-key-${uuid}`} date={new Date(created)} />
    ) : null;
  }

  renderRow = (repository: Repository): JSX.Element => {
    const { uuid, name, links, slug, project } = repository;
    const date = this.getLastUpdatedDate(repository);
    const projectLink =
      project && project.links ? project.links.html.href : null;
    return (
      <styles.RepositoryRow key={uuid} data-testid="profile-repository-row">
        <TableCell widthWithUnit="28%">
          <styles.RepositoryContainer>
            <styles.AvatarWrapper>
              <Avatar
                size="medium"
                appearance="square"
                name={name}
                src={links.avatar.href}
                status={repository.is_private ? 'locked' : null}
                href={links.html.href}
              />
            </styles.AvatarWrapper>
            <styles.RepositoryInfo>
              <styles.RepositoryLink to={links.html.href}>
                {slug}
              </styles.RepositoryLink>
              <styles.ProjectNameUpdatedContainer>
                {!!project && !!project.name && !!projectLink && (
                  <styles.Info>
                    <Link to={projectLink}>{project.name}</Link>
                  </styles.Info>
                )}
                <styles.DateWrapper>
                  {`- `}
                  <FormattedMessage {...messages.updatedLabel} /> {date}
                </styles.DateWrapper>
              </styles.ProjectNameUpdatedContainer>
            </styles.RepositoryInfo>
          </styles.RepositoryContainer>
        </TableCell>
        <TableCellMobileHidden widthWithUnit="45%">
          <styles.DescriptionWrapper>
            <Truncate lines={2}>{repository.description}</Truncate>
          </styles.DescriptionWrapper>
        </TableCellMobileHidden>
        <TableCellMobileHidden widthWithUnit={`${gridSize() * 16}px`}>
          {date}
        </TableCellMobileHidden>
        <TableCell centered widthWithUnit={`${gridSize() * 8}px`}>
          <RepositoryBuildStatus repository={repository} />
        </TableCell>
      </styles.RepositoryRow>
    );
  };

  renderEmptyState = (): JSX.Element => {
    const {
      repositories,
      buildStatusesFetched,
      isLoading,
      isError,
      isFilterApplied,
      createUrl,
      importUrl,
    } = this.props;

    const showEmptyState =
      repositories?.length === 0 &&
      !(isLoading || isError) &&
      buildStatusesFetched;

    if (!showEmptyState) {
      return <React.Fragment />;
    }

    const emptyResultsProps = isFilterApplied
      ? {
          header: messages.noMatchHeader,
          desc: messages.noMatchDesc,
          primaryAction: {
            msg: messages.clearAllFilters,
            href: window.location.pathname,
          },
        }
      : {
          header: messages.noReposHeader,
          desc: messages.noReposDesc,
          primaryAction: createUrl
            ? {
                msg: messages.createLink,
                href: createUrl,
              }
            : undefined,
          secondaryAction: importUrl
            ? {
                msg: messages.importLink,
                href: importUrl,
              }
            : undefined,
        };

    return (
      <tr>
        <td colSpan={3}>
          <EmptyResults {...emptyResultsProps} />
        </td>
      </tr>
    );
  };

  private renderAnnouncerMessage() {
    const {
      isLoading,
      isFilterApplied,
      intl,
      repositories,
      size,
      pagelen,
    } = this.props;

    if (isLoading) {
      if (isFilterApplied) {
        return intl.formatMessage(messages.announceFilteringProcess);
      }
      return intl.formatMessage(messages.announceLoading);
    }

    // `repositories` is the list of repositories and `size` is the
    // total number of repositories in the list for the given workspace.
    const repositoriesCount = repositories ? repositories.length : 0;

    const hasPages = size && pagelen ? size / pagelen > 1 : false;

    if (isFilterApplied && !isLoading) {
      if (repositoriesCount) {
        return intl.formatMessage(
          hasPages
            ? messages.announcePaginatedFilteringRepositories
            : messages.announceFilteringRepositories,
          { count: repositoriesCount }
        );
      }
      return intl.formatMessage(messages.announceFilteringNoRepositoriesFound);
    }

    return intl.formatMessage(messages.announceDisplayingAllRepositories);
  }

  render(): JSX.Element {
    const {
      repositories: rows,
      pagelen,
      size,
      isLoading,
      isError,
      page,
      getPaginationUrl,
    } = this.props;

    const totalPages = size && pagelen ? Math.ceil(size / pagelen) : 1;
    const pages = getPagination(totalPages)(getPaginationUrl);

    const tableProps = {
      isLoading,
      isError,
      rows,
      renderHeaders: this.renderHeaders,
      renderEmptyState: this.renderEmptyState,
      renderPagination: () => (
        <styles.PaginationContainer>
          <WorkspaceRepositoryPagination
            page={page ? Math.max(page, 1) : 1}
            isLoading={isLoading}
            isError={isError}
            pages={pages}
            uiEventSource="WorkspaceRepositoriesScreen"
            uiEventSubjectId="WorkspaceRepositoriesPagination"
          />
        </styles.PaginationContainer>
      ),
      children: this.renderRow,
      renderAnnouncer: () => (
        <Announcer text={this.renderAnnouncerMessage()} delay={200} />
      ),
    };

    if (isLoading) {
      return <PageableTable {...tableProps} />;
    }

    return <InstrumentedProfileRepositoriesTable {...tableProps} />;
  }
}

export default injectIntl(ProfileRepositoriesTable);
